// Typing indicator, the web twin of desktop ThinkingBubble (chat_widgets.py).
// Shown only while waiting for a reply AND only when the block enables the
// TYPING_INDICATOR feature (off by default -> the reply just appears, matching
// desktop). Ports all nine animation styles, their per-style frame intervals,
// the custom base label, and the optional cycling-label mode.

import { useEffect, useRef, useState, type ReactNode } from "react";

export interface TypingIndicatorProps {
  label: string; // base text, e.g. "Thinking"
  style: string; // one of the nine styles below
  fontSizePx: number;
  cycle?: {
    enabled: boolean;
    texts: string[];
    intervalS: number;
    random: boolean;
  };
}

// Per-style frame interval in ms (mirrors desktop _ANIMATION_INTERVALS).
const INTERVALS: Record<string, number> = {
  dots: 350,
  three_dots_pulse: 400,
  wave_dots: 150,
  spinner: 150,
  cursor: 530,
  snake_dots: 250,
  orbit: 50,
  pendulum: 50,
  breathing: 50,
};

const SPINNER_FRAMES = ["|", "/", "—", "\\"];
const DOT_RGB = "33, 37, 41"; // #212529, matches desktop rgba(33,37,41,a)

// Build the animated suffix (everything after the base label) as JSX for the
// given style + frame. Mirrors ThinkingBubble._tick.
function renderFrame(styleName: string, frame: number, label: string): ReactNode {
  switch (styleName) {
    case "dots": {
      const n = frame % 4;
      return <span>{" " + " •".repeat(n)}</span>;
    }
    case "three_dots_pulse": {
      const phase = frame % 4;
      return (
        <span>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{ color: `rgba(${DOT_RGB}, ${i < phase ? 1 : 0.2})`, fontSize: "1.2em" }}
            >
              {"\u00A0•"}
            </span>
          ))}
        </span>
      );
    }
    case "wave_dots": {
      const nPhases = 8;
      return (
        <span>
          {[0, 1, 2].map((i) => {
            const phase = (frame + i * 3) % nPhases;
            const offset = Math.sin((phase * 2 * Math.PI) / nPhases);
            const dot =
              offset < -0.5 ? (
                <sup style={{ fontSize: "1.1em" }}>•</sup>
              ) : offset > 0.5 ? (
                <sub style={{ fontSize: "1.1em" }}>•</sub>
              ) : (
                <span style={{ fontSize: "1.2em" }}>•</span>
              );
            return <span key={i}>{dot} </span>;
          })}
        </span>
      );
    }
    case "spinner": {
      return <span>{" " + SPINNER_FRAMES[frame % SPINNER_FRAMES.length]}</span>;
    }
    case "cursor": {
      return <span>{frame % 2 === 0 ? " █" : "  "}</span>;
    }
    case "snake_dots": {
      const n = 5;
      const head = frame % n;
      return (
        <span>
          {Array.from({ length: n }, (_, i) => {
            const dist = (head - i + n) % n;
            const [size, op] =
              dist === 0 ? [1.2, 1.0] : dist === 1 ? [1.0, 0.7] : dist === 2 ? [0.8, 0.4] : [0.6, 0.0];
            return (
              <span key={i} style={{ color: `rgba(${DOT_RGB}, ${op})`, fontSize: `${size}em` }}>
                {"\u00A0•"}
              </span>
            );
          })}
        </span>
      );
    }
    case "orbit": {
      const nDots = 3;
      const radius = 6;
      const center = 7;
      const width = 15;
      const angleBase = frame * 0.12;
      const cells: ReactNode[] = Array.from({ length: width }, () => "\u00A0");
      for (let i = 0; i < nDots; i++) {
        const angle = angleBase + i * ((2 * Math.PI) / nDots);
        let x = Math.round(center + Math.cos(angle) * radius);
        x = Math.max(0, Math.min(width - 1, x));
        const depth = Math.sin(angle);
        const [dot, op] = depth > 0.3 ? ["●", 1.0] : depth > -0.3 ? ["•", 0.6] : ["·", 0.3];
        cells[x] = (
          <span key={`d${i}`} style={{ color: `rgba(${DOT_RGB}, ${op})` }}>
            {dot}
          </span>
        );
      }
      return (
        <span style={{ fontFamily: "monospace", letterSpacing: "-1px" }}>
          {cells.map((c, i) => (
            <span key={i}>{c}</span>
          ))}
        </span>
      );
    }
    case "pendulum": {
      const x = Math.sin(frame * 0.15) * 3 + 3;
      const pad = "\u00A0".repeat(Math.floor(x));
      return (
        <span>
          {pad}●
        </span>
      );
    }
    case "breathing": {
      const opacity = 0.4 + (Math.sin(frame * 0.12) + 1) * 0.3;
      const text = label || "...";
      // Breathing re-renders the label itself with varying opacity.
      return <span style={{ color: `rgba(${DOT_RGB}, ${opacity.toFixed(2)})` }}>{text}</span>;
    }
    default:
      return null;
  }
}

export default function TypingIndicator({ label, style, fontSizePx, cycle }: TypingIndicatorProps) {
  const [frame, setFrame] = useState(0);
  const [baseText, setBaseText] = useState(label);
  const cycleIdx = useRef(0);

  // Animation ticker at the style's interval.
  useEffect(() => {
    const interval = INTERVALS[style] ?? 350;
    const id = window.setInterval(() => setFrame((f) => f + 1), interval);
    return () => clearInterval(id);
  }, [style]);

  // Optional cycling label.
  useEffect(() => {
    if (!cycle?.enabled || cycle.texts.length === 0) return;
    setBaseText(cycle.texts[0]);
    cycleIdx.current = 0;
    const id = window.setInterval(
      () => {
        if (cycle.random) {
          cycleIdx.current = Math.floor(Math.random() * cycle.texts.length);
        } else {
          cycleIdx.current = (cycleIdx.current + 1) % cycle.texts.length;
        }
        setBaseText(cycle.texts[cycleIdx.current]);
      },
      Math.max(1, cycle.intervalS) * 1000,
    );
    return () => clearInterval(id);
  }, [cycle]);

  const spacer = baseText ? " " : "";
  const suffix = renderFrame(style, frame, baseText);

  return (
    <div className="jol-msg-row jol-msg-row--assistant">
      <div
        className="jol-bubble jol-bubble--assistant jol-typing-bubble"
        style={{ fontSize: `${fontSizePx}px`, fontStyle: "italic" }}
        aria-label="Assistant is typing"
      >
        {style === "breathing" ? (
          suffix
        ) : (
          <>
            {baseText}
            {spacer}
            {suffix}
          </>
        )}
      </div>
    </div>
  );
}
